import type { Ref } from 'vue'
import gsap from 'gsap'
import { PEACEMAKER_TYPE, type PEACEMAKERTypeCode } from '~/constants/peacemaker'

const POOL_SIZE = 20
const VISIBLE_COUNT = 6
const TICK_MS = 3000

export interface PeacemakerLiveItem {
  docId: number
  countryCd: string
  countryNm: string
  writerNm: string
  placeType: PEACEMAKERTypeCode
  profileImageUrl?: string
  createdAt: string
}

interface UsePeacemakerLiveFeedOptions {
  liveListRef: Ref<HTMLElement | null>
}

export const usePeacemakerLiveFeed = ({ liveListRef }: UsePeacemakerLiveFeedOptions) => {
  const { locale } = useI18n()
  const runtimeConfig = useRuntimeConfig()

  // 서버 원본과 화면 표시를 분리해 관리한다.
  const feedPool = ref<PeacemakerLiveItem[]>([]) // 서버 원본
  const visibleItems = ref<PeacemakerLiveItem[]>([]) // 화면 표시
  const displayQueue = ref<PeacemakerLiveItem[]>([]) // 화면 표시 대기 큐
  const pendingDeleteIds = ref<Set<number>>(new Set()) // 삭제 대기 항목 ID 집합
  const hasEnoughServerItems = ref(false) // 서버 원본 항목이 충분한지 여부

  // 실시간 스트림과 순환 애니메이션 상태를 관리한다.
  let peacemakerLiveSource: EventSource | null = null // SSE 연결
  let tickTimer: ReturnType<typeof setInterval> | null = null // 타이머
  let poolCycleIndex = 0 // 순환 인덱스
  let isAnimating = false // 애니메이션 상태

  function normalizeLiveItem(raw: Record<string, unknown>): PeacemakerLiveItem {
    // 서버 응답을 화면에서 바로 쓰는 형태로 정규화한다.
    const countryCd = String(raw.countryCd ?? '').toUpperCase()

    return {
      docId: Number(raw.docId),
      countryCd,
      countryNm: formatIntlCountry(countryCd, locale.value),
      writerNm: String(raw.writerNm ?? ''),
      placeType: Number(raw.categoryId ?? PEACEMAKER_TYPE.PEACE) as PEACEMAKERTypeCode,
      profileImageUrl: String(raw.profileImageUrl ?? ''),
      createdAt: String(raw.createdAt ?? ''),
    }
  }

  // 서버에서 사라진 항목은 화면에서 빠진 뒤 정리한다.
  function markPendingDelete(docId: number) {
    const next = new Set(pendingDeleteIds.value)
    next.add(docId)
    pendingDeleteIds.value = next
  }

  // 삭제 대기 항목을 정리한다.
  function purgeDocId(docId: number) {
    if (!pendingDeleteIds.value.has(docId))
      return

    const nextPending = new Set(pendingDeleteIds.value)
    nextPending.delete(docId)
    pendingDeleteIds.value = nextPending

    feedPool.value = feedPool.value.filter(item => item.docId !== docId)
    displayQueue.value = displayQueue.value.filter(item => item.docId !== docId)
  }

  function onItemLeftVisible(docId: number | undefined) {
    if (docId != null && pendingDeleteIds.value.has(docId))
      purgeDocId(docId)
  }

  // 화면에 남아 있는 항목의 최신 필드만 동기화한다.
  function syncVisibleFields(incoming: PeacemakerLiveItem[]) {
    const byId = new Map(incoming.map(item => [item.docId, item]))
    visibleItems.value = visibleItems.value.map((item) => {
      const updated = byId.get(item.docId)
      return updated ? { ...updated } : item
    })
  }

  function enqueueDisplay(item: PeacemakerLiveItem) {
    // 이미 화면이나 큐에 있는 항목은 중복으로 넣지 않는다.
    if (visibleItems.value.some(v => v.docId === item.docId))
      return
    if (displayQueue.value.some(v => v.docId === item.docId))
      return
    displayQueue.value = [item, ...displayQueue.value]
  }

  function mergeFeedFromSse(incoming: PeacemakerLiveItem[]) {
    // 새 SSE 목록을 기존 상태와 병합해 화면 기준점을 최대한 유지한다.
    const incomingPool = incoming.slice(0, POOL_SIZE)
    const incomingIds = new Set(incomingPool.map(item => item.docId))
    const previousPoolIds = new Set(feedPool.value.map(item => item.docId))
    const anchorId = visibleItems.value[0]?.docId
    const anchorIndex = anchorId != null
      ? incomingPool.findIndex(item => item.docId === anchorId)
      : -1

    for (const docId of previousPoolIds) {
      if (!incomingIds.has(docId))
        markPendingDelete(docId)
    }

    if (anchorIndex >= 0) {
      const tailFromServer = incomingPool.slice(anchorIndex)
      const tailIds = new Set(tailFromServer.map(item => item.docId))
      const prefixFromVisible = visibleItems.value.filter(item => !tailIds.has(item.docId))

      feedPool.value = [...prefixFromVisible, ...tailFromServer]
        .filter((item, index, arr) => arr.findIndex(i => i.docId === item.docId) === index)
        .slice(0, POOL_SIZE)
    }
    else {
      feedPool.value = incomingPool
    }

    syncVisibleFields(incomingPool)

    for (const item of incomingPool) {
      if (previousPoolIds.has(item.docId))
        continue
      if (visibleItems.value.some(v => v.docId === item.docId))
        continue
      enqueueDisplay(item)
    }

    displayQueue.value = displayQueue.value
      .map(item => incomingPool.find(i => i.docId === item.docId) ?? item)
      .filter(item => incomingIds.has(item.docId))
      .filter(item => !pendingDeleteIds.value.has(item.docId))
  }

  function pickNextItem(): PeacemakerLiveItem | null {
    // 우선순위 큐를 먼저 소진하고 없으면 풀을 순환하며 다음 항목을 고른다.
    while (displayQueue.value.length) {
      const item = displayQueue.value.shift()!
      if (pendingDeleteIds.value.has(item.docId))
        continue
      return item
    }

    if (!feedPool.value.length)
      return null

    for (let i = 0; i < feedPool.value.length; i++) {
      const index = (poolCycleIndex + i) % feedPool.value.length
      const item = feedPool.value[index]!
      if (pendingDeleteIds.value.has(item.docId))
        continue
      poolCycleIndex = (index + 1) % feedPool.value.length
      return item
    }

    return null
  }

  async function slideInAtTop(item: PeacemakerLiveItem) {
    // 새 항목을 상단에 슬라이드로 추가하고 초과된 마지막 항목을 정리한다.
    if (isAnimating)
      return

    isAnimating = true
    const fellOffDocId = visibleItems.value[VISIBLE_COUNT - 1]?.docId

    visibleItems.value = [item, ...visibleItems.value]
    await nextTick()

    const list = liveListRef.value
    const newLi = list?.querySelector('li:first-child') as HTMLElement | null

    if (!newLi) {
      visibleItems.value = visibleItems.value.slice(0, VISIBLE_COUNT)
      onItemLeftVisible(fellOffDocId)
      isAnimating = false
      return
    }

    const refLi = list?.querySelector('li:nth-child(2)') as HTMLElement | null ?? newLi
    const refStyle = getComputedStyle(refLi)
    const paddingTop = refStyle.paddingTop
    const paddingBottom = refStyle.paddingBottom

    gsap.set(newLi, {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
      opacity: 0,
      overflow: 'hidden',
    })

    gsap.set(newLi, { height: 'auto', paddingTop, paddingBottom })
    const targetHeight = newLi.offsetHeight
    gsap.set(newLi, {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
      opacity: 0,
      overflow: 'hidden',
    })

    await gsap.to(newLi, {
      height: targetHeight,
      paddingTop,
      paddingBottom,
      opacity: 1,
      duration: 0.45,
      ease: 'power2.out',
    })

    gsap.set(newLi, { clearProps: 'height,paddingTop,paddingBottom,overflow,opacity' })

    const overflow = visibleItems.value[VISIBLE_COUNT]
    visibleItems.value = visibleItems.value.slice(0, VISIBLE_COUNT)
    await nextTick()
    onItemLeftVisible(overflow?.docId ?? fellOffDocId)
    isAnimating = false
  }

  async function onTick() {
    const next = pickNextItem()
    if (!next)
      return
    await slideInAtTop(next)
  }

  function resetLiveState() {
    // 표시 조건을 만족하지 않을 때 실시간 상태를 초기화한다.
    feedPool.value = []
    visibleItems.value = []
    displayQueue.value = []
    pendingDeleteIds.value = new Set()
    poolCycleIndex = 0
  }

  function applySsePayload(items: Record<string, unknown>[]) {
    // 첫 수신은 초기 화면을 채우고 이후 수신은 병합 로직으로 처리한다.
    const normalized = items.map(item => normalizeLiveItem(item))
    hasEnoughServerItems.value = normalized.length >= VISIBLE_COUNT

    if (!hasEnoughServerItems.value) {
      resetLiveState()
      return
    }

    if (!visibleItems.value.length) {
      feedPool.value = normalized.slice(0, POOL_SIZE)
      visibleItems.value = feedPool.value.slice(0, VISIBLE_COUNT)
      poolCycleIndex = visibleItems.value.length % Math.max(feedPool.value.length, 1)
      return
    }

    mergeFeedFromSse(normalized)
  }

  // 타이머와 SSE 연결은 한 번만 시작한다.
  function startLiveFeed() {
    if (tickTimer || peacemakerLiveSource)
      return

    // 타이머를 설정하고 SSE 연결을 시작한다.
    tickTimer = setInterval(() => {
      onTick()
    }, TICK_MS)

    // SSE 연결을 시작한다.
    peacemakerLiveSource = new EventSource(`${runtimeConfig.public.napiDomain}/hml/v1/peacemaker/live`)
    peacemakerLiveSource.onmessage = (event: MessageEvent) => {
      const { items } = JSON.parse(event.data)
      if (!Array.isArray(items))
        return
      applySsePayload(items)
    }
  }

  function stopLiveFeed() {
    // 페이지 이탈 시 타이머와 SSE 연결을 정리한다.
    if (tickTimer) {
      clearInterval(tickTimer)
      tickTimer = null
    }
    peacemakerLiveSource?.close()
    peacemakerLiveSource = null
  }

  return {
    visibleItems,
    hasEnoughServerItems,
    startLiveFeed,
    stopLiveFeed,
  }
}
